"use client"

import { useState, useEffect, KeyboardEvent } from "react";
import { motion } from "framer-motion";
import Link from "next/link";
import dynamic from "next/dynamic";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { ImageWithFallback } from "./figma/ImageWithFallback";
import { Phone, Mail, ArrowRight } from "lucide-react";
import {
  ArrowLeft,
  CheckCircle,

} from "lucide-react";
import { products } from "@/data/constants";
import { getIcon, toSlug } from "@/lib/utils";
import { Footer } from "./Footer";
import iTimePlus from "@/assets/ITime.svg";
import KashAi from "@/assets/KashAI.png";
import LuxFleet from "@/assets/Luxfleet.svg";
import SmartContractManager from "@/assets/SmartContractManager.svg";
import AIAnalytics from "@/assets/ai-analytics2.png";
import { WhatsAppIcon } from "@/components/ui/whatsapp-icon"
import { contactInfo } from "@/data/contact"

const ContactPopup = dynamic(() => import("./ContactPopup").then((mod) => mod.ContactPopup), { ssr: false });
const ContactBottomSheet = dynamic(() => import("./ContactBottomSheet").then((mod) => mod.ContactBottomSheet), { ssr: false });

export const ProductsPage = () => {
  const [isContactPopupOpen, setIsContactPopupOpen] = useState(false)
  const [isContactBottomSheetOpen, setIsContactBottomSheetOpen] = useState(false)
  const [isMobile, setIsMobile] = useState(false)

  // Check if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768)
    }
    
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  const handleContactClick = () => {
    if (isMobile) {
      setIsContactBottomSheetOpen(true)
    } else {
      setIsContactPopupOpen(true)
    }
  }

  const handleContactKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault()
      handleContactClick()
    }
  }

  const productImages: Record<string, string> = {
    "iTimePlus": iTimePlus.src,
    "Smart Contract Manager": SmartContractManager.src,
    "KA$H AI": KashAi.src,
    "PRiSM": AIAnalytics.src,
    "Lavender": LuxFleet.src,
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950">
      {/* Ambient Background Effects */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-[var(--color-primary)]/10 rounded-full blur-[120px]" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-[var(--color-primary)]/10 rounded-full blur-[120px]" />
      </div>

      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-slate-900 via-slate-950 to-black text-white py-20 border-b border-slate-800/50">
        <div className="container mx-auto px-6">
          <div className="flex items-center mb-8 cursor-pointer">
            <Link href="/#products">
              <Button
                variant="ghost"
                className="cursor-pointer text-slate-300 hover:text-white hover:bg-white/5 border border-slate-700/50 hover:border-slate-600 mr-4 backdrop-blur-sm"
              >
                <ArrowLeft className="h-5 w-5 mr-2" />
                Back to Home
              </Button>
            </Link>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-5xl md:text-7xl font-bold mb-6 text-white">
            Solutions You Can Launch Now
              
            </h1>
            <div className="w-24 h-1 bg-gradient-to-r from-[var(--color-primary)] to-[var(--color-primary-light)] mx-auto mb-6 rounded-full shadow-lg shadow-[var(--color-primary)]/50" />
            <p className="text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed">
            Secure platforms, apps, and analytics that reduce workload, raise accuracy, and scale with you.
            </p>
          </motion.div>
        </div>
      </div>

      <div className="container mx-auto px-6 py-16">

        {/* Products Grid */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.6 }}
        >
          {products.slice(0, 6).map((product, index) => (
            <motion.div
              key={index}
              className="group"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              whileHover={{ y: -8, scale: 1.02 }}
            >
              <div className="relative">

                <Card className="relative h-full bg-gradient-to-br from-slate-900/95 to-slate-800/95 backdrop-blur-xl border border-slate-700/50 hover:border-[var(--color-primary)]/50 transition-all duration-500 hover:shadow-2xl hover:shadow-[var(--color-primary)]/20 overflow-hidden cursor-pointer">
                  {/* Product Image */}
                  <Link href={`/products/${toSlug(product.title)}`}>
                    <div className="relative h-64 overflow-hidden">
                      <motion.div
                        className="absolute inset-0"
                        whileHover={{ scale: 1.1 }}
                        transition={{ duration: 0.6, ease: "easeOut" }}
                      >
                        <ImageWithFallback
                          src={productImages[product.title] ?? "/placeholder-image.jpg"}
                          alt={product.title}
                          className="w-full h-full object-cover"
                          sizes="(min-width: 1024px) 33vw, (min-width: 768px) 50vw, 100vw"
                          priority={index === 0}
                          fetchPriority={index === 0 ? "high" : "auto"}
                        />
                      </motion.div>

                      {/* Gradient Overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent group-hover:from-black/80 transition-all duration-500" />

                      {/* Product Icon */}
                      <motion.div
                        className="absolute top-4 left-4 w-12 h-12 bg-white/10 backdrop-blur-md rounded-2xl flex items-center justify-center border border-white/20"
                        whileHover={{
                          scale: 1.1,
                          background: "rgba(25, 205, 157, 0.3)",
                          borderColor: "rgba(25, 205, 157, 0.5)"
                        }}
                      >
                        {(() => {
                          const IconComponent = getIcon(product.icon);
                          return <IconComponent className="h-6 w-6 text-white drop-shadow-lg" />;
                        })()}
                      </motion.div>

                      {/* Category Badge */}
                      <Badge
                        variant="outline"
                        className="absolute top-4 right-4 border-white/30 text-white bg-black/20 backdrop-blur-sm"
                      >
                        AI Solution
                      </Badge>

                      {/* Visit/Request Demo Button - Floating on Image */}
                      {product.title === "KA$H AI" ? (
                        <motion.a
                          href="https://www.kash-ai.com/"
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => {
                            e.stopPropagation();
                          }}
                          initial={{ opacity: 0, scale: 0.8 }}
                          whileInView={{ opacity: 1, scale: 1 }}
                          transition={{ duration: 0.6, delay: index * 0.1 + 0.4 }}
                          whileHover={{ scale: 1.05, y: -2 }}
                          whileTap={{ scale: 0.95 }}
                          className="absolute bottom-3 left-3 px-3 py-1.5 rounded-full border-2 border-[var(--color-primary)] text-[var(--color-primary)] font-semibold text-xs hover:bg-[var(--color-primary)] hover:text-slate-950 transition-all duration-300 inline-flex items-center justify-center gap-1.5 group/demo bg-black/40 backdrop-blur-sm z-10"
                        >
                          Visit
                          <ArrowRight className="w-3 h-3 group-hover/demo:translate-x-0.5 transition-transform" />
                        </motion.a>
                      ) : (
                        <motion.button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleContactClick();
                          }}
                          initial={{ opacity: 0, scale: 0.8 }}
                          whileInView={{ opacity: 1, scale: 1 }}
                          transition={{ duration: 0.6, delay: index * 0.1 + 0.4 }}
                          whileHover={{ scale: 1.05, y: -2 }}
                          whileTap={{ scale: 0.95 }}
                          className="absolute bottom-3 left-3 px-3 py-1.5 rounded-full border-2 border-[var(--color-primary)] text-[var(--color-primary)] font-semibold text-xs hover:bg-[var(--color-primary)] hover:text-slate-950 transition-all duration-300 flex items-center justify-center gap-1.5 group/demo bg-black/40 backdrop-blur-sm z-10"
                        >
                          Request Demo
                          <ArrowRight className="w-3 h-3 group-hover/demo:translate-x-0.5 transition-transform" />
                        </motion.button>
                      )}
                    </div>
                  </Link>

                  <CardContent className="p-6 space-y-4">
                    {/* Title & Subtitle */}

                    <div>
                      <h2 className="text-xl font-bold text-white group-hover:bg-gradient-to-r group-hover:from-[var(--color-primary)] group-hover:to-[var(--color-primary-light)] group-hover:bg-clip-text group-hover:text-transparent transition-all duration-300 mb-2">
                        
                        {product.title === "KA$H AI" ? (
                          <>
                            KA<span className="text-[#4ebf8a]">$</span>H AI
                          </>
                        ) : (
                          product.title
                        )}
                      </h2>
                      <p className="text-sm text-[var(--color-primary)] font-semibold mb-3 line-clamp-1">
                        {product.subtitle}
                      </p>
                      <p className="text-slate-400 leading-relaxed group-hover:text-slate-300 transition-colors duration-300 line-clamp-1">
                        {product.description}
                      </p>
                    </div>

                    {/* Features */}
                    <div className="space-y-3">
                      <div className="flex items-center text-sm font-semibold bg-gradient-to-r from-[var(--color-primary)] to-[var(--color-primary-light)] bg-clip-text text-transparent mb-3">
                        <CheckCircle className="h-4 w-4 mr-2 text-[var(--color-primary)]" />
                        Key Features
                      </div>
                      <div className="grid grid-cols-1 gap-2">
                        {product.features.slice(0, 4).map((feature, featureIndex) => (
                          <motion.div
                            key={featureIndex}
                            className="flex items-center text-sm text-slate-400 group-hover:text-slate-300 transition-colors duration-300"
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.4, delay: featureIndex * 0.1 }}
                          >
                            <div className="w-2 h-2 bg-[var(--color-primary)] rounded-full mr-3 flex-shrink-0 shadow-[0_0_8px_rgba(25,205,157,0.6)]"></div>
                            <span>{feature}</span>
                          </motion.div>
                        ))}
                      </div>
                    </div>

                    {/* Hover Glow Effect */}
                    <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none">
                      <div className="absolute inset-0 bg-gradient-to-br from-[var(--color-primary)]/5 via-transparent to-[var(--color-primary-light)]/5 rounded-lg" />
                      <div className="absolute -inset-1 bg-gradient-to-r from-[var(--color-primary)]/10 via-[var(--color-primary-light)]/10 to-[var(--color-primary)]/10 rounded-lg blur-xl" />
                    </div>
                    <motion.div
                      className="pt-6 border-t border-slate-700/50 group-hover:border-[var(--color-primary)]/20 transition-colors duration-500"
                      initial={{ opacity: 0 }}
                      whileInView={{ opacity: 1 }}
                      transition={{ duration: 0.6, delay: index * 0.1 + 0.8 }}
                    >
                      <div className="text-center mb-4">
                        <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Get in Touch</p>
                      </div>

                      <div className="flex items-center justify-center space-x-4">
                        {/* WhatsApp Icon */}
                        <motion.a
                          href={contactInfo.whatsapp.ksa.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label="Chat with us on WhatsApp"
                          initial={{ opacity: 0, scale: 0.8 }}
                          whileInView={{ opacity: 1, scale: 1 }}
                          transition={{ duration: 0.6, delay: index * 0.1 + 0.8 }}
                          whileHover={{ scale: 1.1, y: -2 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            window.open(contactInfo.whatsapp.ksa.href, "_blank");
                          }}
                          className="w-11 h-11 border border-slate-700 hover:border-[#25D366] rounded-full flex items-center justify-center transition-all duration-300 bg-slate-800/50 hover:bg-[#25D366]/10 backdrop-blur-sm cursor-pointer"
                        >
                          <WhatsAppIcon className="h-5 w-5 text-slate-400 hover:text-[#25D366] transition-colors duration-300" />
                        </motion.a>

                        {/* Phone Icon - Opens Popup/Bottom Sheet */}
                        <motion.div
                          initial={{ opacity: 0, scale: 0.8 }}
                          whileInView={{ opacity: 1, scale: 1 }}
                          transition={{ duration: 0.6, delay: index * 0.1 + 0.9 }}
                          whileHover={{ scale: 1.1, y: -2 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleContactClick();
                          }}
                          onKeyDown={handleContactKeyDown}
                          role="button"
                          tabIndex={0}
                          aria-label="Request a call"
                          className="w-11 h-11 border border-slate-700 hover:border-[var(--color-primary)] rounded-full flex items-center justify-center transition-all duration-300 bg-slate-800/50 hover:bg-[var(--color-primary)]/10 backdrop-blur-sm cursor-pointer"
                        >
                          <Phone className="h-5 w-5 text-slate-400 hover:text-[var(--color-primary)] transition-colors duration-300" />
                        </motion.div>

                        {/* Email Icon */}
                        <motion.a
                          href={`mailto:${contactInfo.email}`}
                          aria-label="Email us"
                          initial={{ opacity: 0, scale: 0.8 }}
                          whileInView={{ opacity: 1, scale: 1 }}
                          transition={{ duration: 0.6, delay: index * 0.1 + 1.0 }}
                          whileHover={{ scale: 1.1, y: -2 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            window.location.href = `mailto:${contactInfo.email}`
                          }}
                          className="w-11 h-11 border border-slate-700 hover:border-blue-500 rounded-full flex items-center justify-center transition-all duration-300 bg-slate-800/50 hover:bg-blue-500/10 backdrop-blur-sm cursor-pointer"
                        >
                          <Mail className="h-5 w-5 text-slate-400 hover:text-blue-500 transition-colors duration-300" />
                        </motion.a>
                      </div>
                    </motion.div>
                  </CardContent>
                </Card>



                {/* WhatsApp Contact - Outside Link to avoid nested <a> tags */}

              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>

      {/* Contact Popup */}
      <ContactPopup
        isOpen={isContactPopupOpen}
        onClose={() => setIsContactPopupOpen(false)}
      />

      {/* Contact Bottom Sheet */}
      <ContactBottomSheet
        isOpen={isContactBottomSheetOpen}
        onClose={() => setIsContactBottomSheetOpen(false)}
      />

      <Footer />
    </div>
  );
};
